import React from 'react';

/**
 * VOHK Logo Component
 * 
 * A reusable, theme-aware logo component that automatically displays
 * the appropriate logo version based on the background context.
 * 
 * @param {Object} props
 * @param {string} props.size - Size preset: 'xs' (16px), 'sm' (24px), 'md' (32px), 'lg' (48px), 'xl' (64px), '2xl' (96px), '3xl' (128px)
 * @param {number} props.height - Custom height in pixels (overrides size preset)
 * @param {number} props.width - Custom width in pixels (overrides size preset)
 * @param {string} props.variant - 'auto' (default), 'maroon', or 'white'
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.dark - Force dark background mode (shows white logo)
 */
export function VOHKLogo({ 
  size = 'md', 
  height, 
  width, 
  variant = 'auto',
  className = '',
  dark = false,
  ...props 
}) {
  // Size presets in pixels
  const sizePresets = {
    'xs': 16,
    'sm': 24,
    'md': 32,
    'lg': 48,
    'xl': 64,
    '2xl': 96,
    '3xl': 128,
  };

  // Determine the actual height to use
  const actualHeight = height || sizePresets[size] || 32;
  
  // Calculate width to maintain aspect ratio (858:881 ≈ 0.974)
  const aspectRatio = 858 / 881;
  const actualWidth = width || Math.round(actualHeight * aspectRatio);

  // Determine which logo variant to use
  const getLogoSrc = () => {
    if (variant === 'maroon') return '/logo-maroon.png';
    if (variant === 'white') return '/logo-white.png';
    // Auto mode: use dark prop or check if we should show white
    if (dark) return '/logo-white.png';
    return '/logo-maroon.png';
  };

  return (
    <img
      src={getLogoSrc()}
      alt="VOHK"
      className={`object-contain ${className}`}
      style={{
        height: actualHeight,
        width: actualWidth,
      }}
      {...props}
    />
  );
}

/**
 * VOHK Logo with text
 * Displays logo alongside VOHK text
 */
export function VOHKLogoWithText({
  size = 'md',
  variant = 'auto',
  dark = false,
  className = '',
}) {
  const textSizes = {
    'xs': { title: 'text-sm' },
    'sm': { title: 'text-base' },
    'md': { title: 'text-lg' },
    'lg': { title: 'text-xl' },
    'xl': { title: 'text-2xl' },
    '2xl': { title: 'text-3xl' },
    '3xl': { title: 'text-4xl' },
  };

  const { title } = textSizes[size] || textSizes.md;
  const textColor = dark ? 'text-white' : 'text-maroon-950';

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <VOHKLogo size={size} variant={variant} dark={dark} />
      <div>
        <h2 className={`${title} font-bold tracking-tight ${textColor}`}>VOHK</h2>
      </div>
    </div>
  );
}

export default VOHKLogo;
